import { Editor } from "@tiptap/core";
import { compact, toInteger, uniqBy } from "lodash";
import moment from "moment";
import {
  CreateTopicNewPageMutationMutation,
  CreateTopicNewPageMutationMutationVariables,
  MeetingViewMeetingNodeNewPageFragmentFragment,
  NotesRequirement,
  TopicState,
} from "types/graphql-schema";

import { getLabel } from "@apps/use-label/use-label";
import { currentOrganizationVar, currentUserVar } from "@cache/cache";
import client from "@graphql/client";

import { defaultTopicTitle } from "./components/topic";
import createOrUpdateTopicMutation from "./graphql/create-or-update-topic-mutation";
import TopicNodeFragment from "./graphql/topic-node-fragment";

export const meetingPaddingClassName = "px-6";

const currentMeetingOption = {
  value: "current-meeting-group",
  label: "in this meeting series",
};
const allMeetingsOption = {
  value: "all-meetings",
  label: "all meetings",
};
export const searchContextOptions = [currentMeetingOption, allMeetingsOption];

export type MeetingOrTemplateVisibility = {
  allowAdminVisibility: boolean;
  allowOrgVisibility: boolean;
  allowManagementTreeVisibility: boolean;
};

export const getMeetingAllowVisibilityLabel = (
  {
    allowAdminVisibility,
    allowOrgVisibility,
    allowManagementTreeVisibility,
  }: MeetingOrTemplateVisibility,
  shortVersion = false,
): string => {
  const label = getLabel();
  const orgLabel = label("organization");
  const currentOrganization = currentOrganizationVar();
  if (allowOrgVisibility)
    return shortVersion
      ? `${orgLabel} members`
      : `all ${currentOrganization.name} members`;
  const parts = shortVersion
    ? compact([
        "participants",
        allowManagementTreeVisibility && "management tree",
        allowAdminVisibility && `admins`,
      ])
    : compact([
        "meeting participants",
        allowManagementTreeVisibility && "participants' management tree",
        allowAdminVisibility && `${currentOrganization.name} admins`,
      ]);
  if (parts.length === 1) {
    return parts.join("") + " only";
  }
  const last = parts.pop();
  return parts.join(", ") + " and " + last;
};

// Walk up the DOM to the nearest ancestor that scrolls vertically.
// Falls back to the document scrolling element.
const getScrollingAncestor = (el: Element): Element => {
  let parent: Element | null = el.parentElement;
  while (parent) {
    const overflowY = getComputedStyle(parent).overflowY;
    if (overflowY === "auto" || overflowY === "scroll") return parent;
    parent = parent.parentElement;
  }
  return document.scrollingElement || document.documentElement;
};

// Scroll `el` into view, leaving `marginBottom` of breathing room below
// it (so the focused editor isn't flush against the viewport edge).
const scrollIntoViewWithBottomMargin = (el: Element, marginBottom = 24) => {
  const scroller = getScrollingAncestor(el);
  const scrollerRect = scroller.getBoundingClientRect();
  const elRect = el.getBoundingClientRect();
  const elTopRelative = elRect.top - scrollerRect.top;
  const elBottomRelative = elRect.bottom - scrollerRect.top;
  const viewportHeight = scroller.clientHeight;

  if (elTopRelative < 0) {
    scroller.scrollBy({ top: elTopRelative, behavior: "smooth" });
  } else if (elBottomRelative > viewportHeight - marginBottom) {
    const delta = elBottomRelative - (viewportHeight - marginBottom);
    scroller.scrollBy({ top: delta, behavior: "smooth" });
  }
};

// Poll for a Tiptap editor matching `editorSelector` inside the topic
// container, then focus + scroll it into view. Used after the
// "Insert shared / my notes" mutation resolves — the editor still
// takes a beat to mount and Yjs to initialize, so we poll until it
// becomes editable (or the timeout expires).
export const focusTopicEditorWhenReady = (
  topicId: number,
  editorSelector: string,
  timeoutMs = 3000,
) => {
  const startTime = performance.now();
  const tryFocus = () => {
    const container = document.querySelector(
      `[data-testid="container-topic-${topicId}"]`,
    );
    const el = container?.querySelector(editorSelector) as
      | (Element & { editor?: Editor })
      | null;
    if (el?.editor?.isEditable) {
      scrollIntoViewWithBottomMargin(el, 24);
      el.editor.chain().focus("end").run();
      return;
    }
    if (performance.now() - startTime < timeoutMs) {
      requestAnimationFrame(tryFocus);
    }
  };
  requestAnimationFrame(tryFocus);
};

export const focusOnTopicTitleInput = (topicId: number, meetingId: number) => {
  const id = `#meeting-${meetingId}-topic-${topicId}`;
  const container = document.querySelector(id);
  if (!container) return;
  // If user is already editing with custom text, don't override
  const editInput = container.querySelector(
    '[aria-label="Topic title"]',
  ) as HTMLInputElement | null;
  if (editInput && !editInput.disabled) {
    editInput.focus();
  }
};

export const createTopicInMeeting = ({
  meeting,
  afterTopicId,
  beforeTopicId,
}: {
  meeting: MeetingViewMeetingNodeNewPageFragmentFragment;
  afterTopicId?: number;
  beforeTopicId?: number;
}) => {
  const currentUser = currentUserVar();
  const clientId = -toInteger(Date.now() / 1000);
  const isFormalOneonone = !!meeting.meetingGroup?.isFormalOneonone;
  const topicData = {
    id: clientId,
    clientId,
    title: defaultTopicTitle,
    description: "",
    includesIndividualNotes: isFormalOneonone,
    includesIndividualNotesForCurrentUser: isFormalOneonone,
    includesSharedNotes: !isFormalOneonone,
    discussionNotes: null,
    isMandatory: false,
    canUpdate: { __typename: "PermissionNode" as const, permission: true },
    canDelete: { __typename: "PermissionNode" as const, permission: true },
    created: moment().format(),
    state: TopicState.Open,
    eventChannelName: `topic-${clientId}`,
    creator: currentUser,
    assignee: currentUser,
    previousTopic: null,
    linkedTemplateTopic: null,
    notesRequirement: NotesRequirement.NotRequired,
    comments: { totalCount: 0, __typename: "CommentNodeConnection" as const },
    individualNotes: {
      edges: [],
      __typename: "IndividualNoteNodeConnection" as const,
    },
    copiedFrom: null,
    __typename: "TopicNode" as const,
  };
  client.mutate<
    CreateTopicNewPageMutationMutation,
    CreateTopicNewPageMutationMutationVariables
  >({
    mutation: createOrUpdateTopicMutation,
    variables: {
      meetingId: meeting.id,
      title: topicData.title,
      afterTopicId,
      beforeTopicId,
      includesIndividualNotes: isFormalOneonone,
      includesSharedNotes: !isFormalOneonone,
    },
    optimisticResponse: {
      __typename: "Mutation",
      createOrUpdateTopic: {
        topic: {
          ...topicData,
        },
        __typename: "CreateOrUpdateTopicMutation",
      },
    },
    update(cache, response) {
      const newTopic = response?.data?.createOrUpdateTopic?.topic;
      if (!newTopic) return;
      const createdTopicCacheNode = cache.writeFragment({
        // DO NOT REMOVE THE CLIENT ID HERE
        // WE WANT TO PRESERVE THE ONE PASSED IN THE OPTIMISTIC RESPONSE
        data: { ...newTopic, clientId },
        fragment: TopicNodeFragment,
        fragmentName: "TopicNodeNewPageFragment",
        variables: { meetingGroupId: meeting.meetingGroupId },
      });
      const meetingCacheId = cache.identify(meeting);
      cache.modify({
        id: meetingCacheId,
        fields: {
          topics(cachedTopics) {
            const afterTopicRef = afterTopicId
              ? cache.identify({
                  __typename: "TopicNode",
                  id: afterTopicId,
                })
              : null;
            const beforeTopicRef = beforeTopicId
              ? cache.identify({
                  __typename: "TopicNode",
                  id: beforeTopicId,
                })
              : null;
            const mergedEdges = afterTopicRef
              ? cachedTopics.edges.reduce((memo: any[], cachedTopic: any) => {
                  if (cachedTopic.node.__ref === afterTopicRef) {
                    return [
                      ...memo,
                      cachedTopic,
                      {
                        node: createdTopicCacheNode,
                        __typename: "TopicNodeEdge",
                      },
                    ];
                  }
                  return [...memo, cachedTopic];
                }, [])
              : beforeTopicRef
                ? cachedTopics.edges.reduce((memo: any[], cachedTopic: any) => {
                    if (cachedTopic.node.__ref === beforeTopicRef) {
                      return [
                        ...memo,
                        {
                          node: createdTopicCacheNode,
                          __typename: "TopicNodeEdge",
                        },
                        cachedTopic,
                      ];
                    }
                    return [...memo, cachedTopic];
                  }, [])
                : [
                    ...cachedTopics.edges,
                    {
                      node: createdTopicCacheNode,
                      __typename: "TopicNodeEdge",
                    },
                  ];
            const uniqueEdges = uniqBy(mergedEdges, ({ node }) => node.__ref);
            return { edges: uniqueEdges };
          },
        },
      });
    },
  });
};
